import ArrowStyleLeft from "../../assets/icons/ArrowStyle/ArrowStyleLeft";
import Loading from "../../assets/icons/Loading";
import PurchaseForm from "../../components/PurchaseForm/PurchaseForm";
import {
  countQuantityCart,
  getProductById,
  updateCart,
} from "../../services/cartShoppingService";
import { getUser } from "../../services/userService";
import { refreshAccessToken } from "../../store/slice/authSlice";
import { AppDispatch, RootState } from "../../store/store";
import { displayNumber, getPrice } from "../../utils/getPrice";
import "./Purchase.scss";
import { fetchAddresses } from "./Purchase.tools";
import React from "react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const DELIVERY_PRICES: any = {
  standard: 25,
  premium: 55,
  retailer: 0,
};

const _computeTotalPrice = (
  shoppingCart: any,
  products: any,
  deliveryMode: string,
  reduc: number
) => {
  let price = 0,
    tva = 0;

  shoppingCart.forEach((item: any) => {
    if (products?.[item.productCode]) {
      price +=
        item.quantity *
        Number(getPrice(products[item.productCode].PrixHT, reduc));
      tva +=
        (item.quantity *
          Number(getPrice(products[item.productCode].PrixHT, reduc)) *
          products[item.productCode].TVATaux) /
        100;
    }
  });
  return {
    price,
    tva,
    total: price + tva + (DELIVERY_PRICES?.[deliveryMode] || 0),
  };
};

const _computeFutureDeliveryDates = (wantedDates: Array<number>) => {
  const result: any = {};
  let timing = new Date();
  let openDayIndex = 0;

  while (openDayIndex < Math.max(...wantedDates)) {
    timing.setDate(timing.getDate() + 1);
    if ([0, 6].includes(timing.getDay())) {
      continue;
    } else {
      openDayIndex++;
    }
    if (wantedDates.includes(openDayIndex)) {
      result[openDayIndex] = new Date(timing).toLocaleDateString();
    }
  }
  return result;
};

const Purchase = () => {
  const [loading, setLoading] = useState(true);
  const { shoppingCart } = useSelector((state: RootState) => state.cart);
  const navigate = useNavigate();
  const [acceptedConditions, setAcceptedConditions] = useState(false);
  const [deliveryMode, setDeliveryMode] = useState("standard");
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [products, setProducts] = useState<any>({});
  const [addresses, setAddresses] = useState<any>({});
  const [deliveryAddress, setDeliveryAddress] = useState<any>({});
  const [billingAddress, setBillingAddress] = useState<any>({});
  const [showSelectAddresses, setShowSelectAddresses] = useState<string | null>(
    null
  );
  const dispatch = useDispatch<AppDispatch>();
  const { access_token } = useSelector((state: RootState) => state.auth);

  const { user } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    if (access_token && !user?.id) {
      dispatch(getUser(access_token));
    }
  }, [user, access_token, dispatch]);

  useEffect(() => {
    dispatch(refreshAccessToken());
  }, [dispatch]);

  useEffect(() => {
    if (shoppingCart?.length > 0) {
      shoppingCart.forEach((item) => {
        if (!Object.keys(products).includes(item.productCode)) {
          getProductById(item.productCode).then((res) =>
            setProducts((products: any) => ({
              ...products,
              [item.productCode]: res,
            }))
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shoppingCart]);

  useEffect(() => {
    fetchAddresses(setAddresses, setLoading, () =>
      dispatch(refreshAccessToken())
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (addresses.length) {
      setDeliveryAddress(
        addresses.find((address: any) => !!address.isDefaultShipping) ||
          addresses[0]
      );
      setBillingAddress(
        addresses.find((address: any) => !!address.isDefaultBilling) ||
          addresses[0]
      );
    }
  }, [addresses]);

  const handleDeliveryChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setDeliveryMode(event.target.value);
  const dates = _computeFutureDeliveryDates([3, 8]);

  const pricing = useMemo(
    () =>
      _computeTotalPrice(
        shoppingCart,
        products,
        deliveryMode,
        user?.discountRate ?? 0
      ),
    [shoppingCart, products, deliveryMode, user?.discountRate]
  );

  const onOrder = () => {
    try {
      if (access_token) {
        dispatch(updateCart({ cart: shoppingCart, token: access_token }));
        setShowPurchaseForm(true);
      }
    } catch (error) {
      console.error("Une erreur inattendue est survenue", error);
    }
  };

  return (
    <React.Fragment>
      <div id="purchase">
        <p
          id="return-link"
          className="clickable"
          onClick={() => navigate("/order")}
        >
          <ArrowStyleLeft /> Retour au panier
        </p>
        <div>
          <div id="purchase-divs">
            <div>
              <h2>1. Adresse de livraison</h2>
              <div className="purchase-block">
                {loading ? (
                  <div className="loading">
                    <Loading />
                  </div>
                ) : (
                  <React.Fragment>
                    <div className="address-lines">
                      <p>{deliveryAddress.fullName}</p>
                      <p>{deliveryAddress.addressLine1}</p>
                      <p>{deliveryAddress.addressLine2}</p>
                      <p>
                        {deliveryAddress.postalCode} {deliveryAddress.city}
                      </p>
                      <p>{deliveryAddress.country}</p>
                      <p>{deliveryAddress.phone}</p>
                    </div>
                    <div className="align-self-end">
                      <span
                        className="clickable"
                        onClick={() => setShowSelectAddresses("delivery")}
                      >
                        Modifier
                      </span>
                    </div>
                  </React.Fragment>
                )}
              </div>

              <h2>2. Mode de livraison</h2>
              <div
                className="purchase-block clickable"
                onClick={() => setDeliveryMode("standard")}
              >
                <div className="radio-block">
                  <div>
                    <input
                      name="delivery-mode"
                      type="radio"
                      value="standard"
                      checked={deliveryMode === "standard"}
                      onChange={handleDeliveryChange}
                    />
                  </div>
                  <div>
                    <p>Standard</p>
                    <p className="text-date-estimation">
                      À partir du {dates[3].toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="align-self-center">
                  <b>25,00€</b>
                </div>
              </div>
              <div
                className="purchase-block clickable"
                onClick={() => setDeliveryMode("premium")}
              >
                <div className="radio-block">
                  <div>
                    <input
                      name="delivery-mode"
                      type="radio"
                      value="premium"
                      checked={deliveryMode === "premium"}
                      onChange={handleDeliveryChange}
                    />
                  </div>
                  <div>
                    <p>Dépoté et rangé</p>
                    <p className="text-date-estimation">
                      À partir du {dates[3].toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="align-self-center">
                  <b>55,00€</b>
                </div>
              </div>
              <div
                className="purchase-block clickable"
                onClick={() => setDeliveryMode("retailer")}
              >
                <div className="radio-block">
                  <div>
                    <input
                      name="delivery-mode"
                      type="radio"
                      value="retailer"
                      checked={deliveryMode === "retailer"}
                      onChange={handleDeliveryChange}
                    />
                  </div>
                  <div>
                    <p>Retrait</p>
                    <p className="text-date-estimation">
                      À partir du {dates[8].toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="align-self-center">
                  <b>Gratuit</b>
                </div>
              </div>

              <h2>3. Adresse de facturation</h2>
              <div className="purchase-block">
                {loading ? (
                  <div className="loading">
                    <Loading />
                  </div>
                ) : (
                  <React.Fragment>
                    <div className="address-lines">
                      <p>{billingAddress.fullName}</p>
                      <p>{billingAddress.addressLine1}</p>
                      <p>{billingAddress.addressLine2}</p>
                      <p>
                        {billingAddress.postalCode} {billingAddress.city}
                      </p>
                      <p>{billingAddress.country}</p>
                      <p>{billingAddress.phone}</p>
                    </div>
                    <div className="align-self-end">
                      <span
                        className="clickable"
                        onClick={() => setShowSelectAddresses("billing")}
                      >
                        Modifier
                      </span>
                    </div>
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>
          <div>
            <div className="recap-cart">
              <h3>Récapitulatif de la commande</h3>
              <div className="article">
                <span>Articles ({countQuantityCart(shoppingCart)})</span>
              </div>
              <div>
                <span>Total (HT)</span>
                <span>{displayNumber(pricing.price)} €</span>
              </div>
              <div>
                <span>Dont TVA</span>
                <span>{displayNumber(pricing.tva)} €</span>
              </div>
              <div>
                <span>Livraison</span>
                <span>{DELIVERY_PRICES?.[deliveryMode]} €</span>
              </div>
              {user?.discountRate && (
                <div className="delivery">
                  Réduction appliquée de {user?.discountRate}%
                </div>
              )}
              <span />
              <div className="separator" />
              <div className="bold">
                <span>Total (TTC)</span>
                <span>{displayNumber(pricing.total)} €</span>
              </div>
              <div>
                <label>
                  <input
                    type="checkbox"
                    onChange={() => setAcceptedConditions((value) => !value)}
                  />
                  J’accepte et je déclare avoir pris connaissance des conditions
                  générales de vente SP Hygiène et être informé de mon droit de
                  rétraction
                </label>
              </div>
              <div>
                <button disabled={!acceptedConditions} onClick={onOrder}>
                  Payer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showPurchaseForm && (
        <div
          id="overlay-purchase-form"
          onClick={() => setShowPurchaseForm(false)}
        >
          <PurchaseForm
            amount={pricing.total}
            billingAddressId={billingAddress.id}
            deliveryAddressId={deliveryAddress.id}
            deliveryMode={deliveryMode}
          />
        </div>
      )}
      {!!showSelectAddresses && (
        <div
          id="addresses-modal-overlay"
          onClick={() => setShowSelectAddresses(null)}
        >
          <div
            id="addresses-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h2>
              Choisir l'adresse de{" "}
              {showSelectAddresses === "delivery" ? "livraison" : "facturation"}
            </h2>
            <div>
              {addresses.map((address: any, index: number) => (
                <div
                  className={`address-choice ${
                    address.id ===
                    (showSelectAddresses === "delivery"
                      ? deliveryAddress
                      : billingAddress
                    ).id
                      ? "active"
                      : ""
                  }`}
                  key={`address-choice-${index}`}
                  onClick={() => {
                    (showSelectAddresses === "delivery"
                      ? setDeliveryAddress
                      : setBillingAddress)(address);
                    setShowSelectAddresses(null);
                  }}
                >
                  <p>
                    <b>{address.fullName}</b>
                    <br />
                    {[address.addressLine1, address.addressLine2]
                      .filter((elem) => !!elem.trim())
                      .join(", ")}
                    <br />
                    {billingAddress.postalCode} {billingAddress.city}
                  </p>
                </div>
              ))}
              <div
                className="address-choice"
                onClick={() => {
                  navigate("/profile?address");
                }}
              >
                <p>+ Ajouter une nouvelle adresse</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default Purchase;
